import { Layer, Node, KonvaEventObject } from "konva/lib/Core";
import { Arrow } from "konva/lib/shapes/Arrow";
import { WhiteboardShape, nodeSize, zeroPosition, IPosition } from "../shapes";
import { relativePosition } from "../util";

export const LINK_DRAGENTER = "linkDragenter";
export const LINK_DRAGOVER = "linkDragover";
export const LINK_DRAGLEAVE = "linkDragleave";
export const LINK_DROP = "linkDrop";

export type LinkingKonvaEventObject = KonvaEventObject<DragEvent> & { shape: WhiteboardShape };

const calculateArrowPoints = (shape: Node): { from: IPosition; to: IPosition } => {
  const shapeSize = nodeSize(shape);
  const middleY = shapeSize.height / 2;
  return {
    from: { x: -15, y: middleY },
    to: { x: -40, y: middleY },
  };
};

export const setLinkingEnabled = (shape: WhiteboardShape, enabled: boolean): void => {
  const link = shape.findOne(".linkable-link");
  if (link) {
    if (enabled) {
      shape.addName("linking-enabled");
      link.visible(isLinkVisible(shape));
    } else {
      shape.removeName("linking-enabled");
      link.hide();
    }
  }
};

export const allowLinkingTo = (shape: WhiteboardShape): void => {
  shape.addName("can-link-to-shape");
};

const canLinkToOtherShapes = (layer?: Layer) => layer && layer.find(".can-link-to-shape").length > 1;

const isLinkVisible = (shape: WhiteboardShape) => shape.selected() && canLinkToOtherShapes(shape.getLayer()) && shape.hasName("linking-enabled");

/**
 * Drag & drop events code adjusted from https://konvajs.org/docs/drag_and_drop/Drop_Events.html
 */
export function linkable<Shape extends WhiteboardShape>(shape: Shape): void {
  let initPoints = calculateArrowPoints(shape);

  const line = new Arrow({
    name: "linkable-link",
    stroke: "#81ACFF",
    strokeWidth: 1,
    dash: [5, 5],
    fill: "#81ACFF",
    pointerLength: 7,
    pointerWidth: 7,
    lineCap: "round",
    lineJoin: "round",
    points: [initPoints.from.x, initPoints.from.y, initPoints.to.x, initPoints.to.y],
    draggable: true,
    visible: false,
  });
  shape.add(line);
  allowLinkingTo(shape);

  let previousCursor: string;
  let previousDragOver: { node: Node; container: Node };

  line.on("dragstart", () => {
    const stage = shape.getStage();
    let linkableLayer = stage.findOne<Layer>("#linkable-layer");
    if (!linkableLayer) {
      linkableLayer = new Layer({ id: "linkable-layer" });
      stage.add(linkableLayer);
    }

    line.bezier(true);

    line.moveTo(linkableLayer);
    linkableLayer.draw();
    shape.getLayer().draw();

    previousCursor = document.body.style.cursor;
    document.body.style.cursor = "pointer";
  });

  line.on("dragmove", (evt) => {
    const stage = shape.getStage();
    const pointer = stage.getPointerPosition();
    const relativePointer = relativePosition(stage, pointer);
    const from = { x: initPoints.from.x + shape.x(), y: initPoints.from.y + shape.y() };
    const middleX = (from.x + relativePointer.x) / 2;

    // draw 4-point bezier line from the initial point to where the mouse pointer is
    line.position(zeroPosition).points([from.x, from.y, middleX, from.y, middleX, relativePointer.y, relativePointer.x, relativePointer.y]);

    // fire drag events for the shape under the mouse pointer
    const layer = shape.getLayer();
    let dragOver: { node: Node; container: Node };
    const dragOverNode = layer.getIntersection(pointer);
    if (dragOverNode) {
      const container = dragOverNode.findAncestor(".shape");
      if (container) {
        dragOver = { node: dragOverNode, container };
      }
    }

    if (previousDragOver && dragOver) {
      if (previousDragOver.container !== dragOver.container) {
        previousDragOver.container.fire(LINK_DRAGLEAVE, { type: LINK_DRAGLEAVE, target: previousDragOver.node, evt: evt.evt, shape }, true);
        dragOver.container.fire(LINK_DRAGENTER, { type: LINK_DRAGENTER, target: dragOver.node, evt: evt.evt }, true);
        previousDragOver = dragOver;
      } else {
        previousDragOver.node = dragOver.node;
        previousDragOver.container.fire(LINK_DRAGOVER, { type: LINK_DRAGOVER, target: previousDragOver.node, evt: evt.evt, shape }, true);
      }
    } else if (!previousDragOver && dragOver) {
      dragOver.container.fire(LINK_DRAGENTER, { type: LINK_DRAGENTER, target: dragOver.node, evt: evt.evt, shape }, true);
      previousDragOver = dragOver;
    } else if (previousDragOver && !dragOver) {
      previousDragOver.container.fire(LINK_DRAGLEAVE, { type: LINK_DRAGLEAVE, target: previousDragOver.node, evt: evt.evt, shape }, true);
      previousDragOver = undefined;
    }
  });

  line.on("dragend", (evt) => {
    const stage = shape.getStage();
    const linkableLayer = stage.findOne<Layer>("#linkable-layer");
    line.bezier(false).position(zeroPosition).points([initPoints.from.x, initPoints.from.y, initPoints.to.x, initPoints.to.y]).moveTo(shape);
    shape.getLayer().batchDraw();
    linkableLayer.batchDraw();

    document.body.style.cursor = previousCursor;
    previousCursor = undefined;

    if (previousDragOver) {
      previousDragOver.container.fire(LINK_DROP, { type: LINK_DROP, target: previousDragOver.node, evt: evt.evt, shape }, true);
    }
    previousDragOver = undefined;
  });

  shape.on("selectedChange", () => {
    line.visible(isLinkVisible(shape));
  });

  shape.on("resize", () => {
    initPoints = calculateArrowPoints(shape);
    line.points([initPoints.from.x, initPoints.from.y, initPoints.to.x, initPoints.to.y]);
    shape.getLayer().batchDraw();
  });
}
